import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { eqci, getDefaultToken, roundUpUSDCBaseAmountToNearest } from "../../../anyspend/index.js";
import { USDC_BASE, ZERO_ADDRESS } from "../../../anyspend/constants/index.js";
import { useAnyspendCreateOnrampOrder, useAnyspendCreateOrder, useAnyspendOrderAndTransactions, useAnyspendQuote, useAnyspendTokenList, useConnectedUserProfile, useGeoOnrampOptions } from "../../../anyspend/react/index.js";
import { Badge, ShinyButton, Skeleton, StyleRoot, Tabs, TabsContent, TextShimmer, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TransitionPanel, useAccountWallet, useHasMounted, useProfile, useRouter, useSearchParamsSSR, useTokenBalancesByChain } from "../../../global-account/react/index.js";
import { cn, formatUsername } from "../../../shared/utils/index.js";
import { toast } from "../../../global-account/react/index.js";
import { shortenAddress } from "../../../shared/utils/formatAddress.js";
import { formatTokenAmount, formatUnits } from "../../../shared/utils/number.js";
import { simpleHashChainToChainName } from "../../../shared/utils/simplehash.js";
import invariant from "invariant";
import { ChevronRight, ChevronRightCircle, Info, Loader2 } from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { base } from "viem/chains";
import { useCryptoPaymentMethodState } from "../hooks/useCryptoPaymentMethodState.js";
import { useOnOrderSuccess } from "../hooks/useOnOrderSuccess.js";
import { useRecipientAddressState } from "../hooks/useRecipientAddressState.js";
import { AnySpendFingerprintWrapper, getFingerprintConfig } from "./AnySpendFingerprintWrapper.js";
import { AnySpendCustomizationProvider } from "./context/AnySpendCustomizationContext.js";
import { CryptoPaymentMethod, CryptoPaymentMethodType } from "./common/CryptoPaymentMethod.js";
import { CryptoPaymentMethodDisplay } from "./common/CryptoPaymentMethodDisplay.js";
import { FeeBreakDown } from "./common/FeeBreakDown.js";
import { FIAT_PAYMENT_METHOD_DISPLAY, FiatPaymentMethod, FiatPaymentMethodComponent } from "./common/FiatPaymentMethod.js";
import { OrderDetails } from "./common/OrderDetails.js";
import { OrderHistory } from "./common/OrderHistory.js";
import { OrderToken } from "./common/OrderToken.js";
import { PointsBadge } from "./common/PointsBadge.js";
import { PointsDetailPanel } from "./common/PointsDetailPanel.js";
import { RecipientSelection } from "./common/RecipientSelection.js";
var PanelView;
(function (PanelView) {
  PanelView[PanelView["CONFIRM_ORDER"] = 0] = "CONFIRM_ORDER";
  PanelView[PanelView["HISTORY"] = 1] = "HISTORY";
  PanelView[PanelView["ORDER_DETAILS"] = 2] = "ORDER_DETAILS";
  PanelView[PanelView["LOADING"] = 3] = "LOADING";
  PanelView[PanelView["RECIPIENT_SELECTION"] = 4] = "RECIPIENT_SELECTION";
  PanelView[PanelView["CRYPTO_PAYMENT_METHOD"] = 5] = "CRYPTO_PAYMENT_METHOD";
  PanelView[PanelView["FIAT_PAYMENT_METHOD"] = 6] = "FIAT_PAYMENT_METHOD";
  PanelView[PanelView["POINTS_DETAIL"] = 7] = "POINTS_DETAIL";
})(PanelView || (PanelView = {}));
function generateGetRelayQuoteRequest({
  orderType,
  srcChainId,
  srcToken,
  dstChainId,
  dstToken,
  recipientAddress,
  dstAmount,
  contractAddress,
  tokenId,
  contractType,
  encodedData,
  spenderAddress,
  onrampVendor
}) {
  switch (orderType) {
    case "mint_nft":
      {
        invariant(contractType, "Contract type is required");
        return {
          type: "mint_nft",
          srcChain: srcChainId,
          srcTokenAddress: srcToken.address,
          dstChain: dstChainId,
          dstTokenAddress: dstToken.address,
          recipientAddress,
          price: dstAmount,
          contractAddress: contractAddress,
          tokenId: tokenId,
          contractType: contractType,
          onrampVendor
        };
      }
    case "join_tournament":
      {
        return {
          type: "join_tournament",
          srcChain: srcChainId,
          srcTokenAddress: srcToken.address,
          dstChain: dstChainId,
          dstTokenAddress: dstToken.address,
          recipientAddress,
          price: dstAmount,
          contractAddress: contractAddress,
          onrampVendor
        };
      }
    case "fund_tournament":
      {
        return {
          type: "fund_tournament",
          srcChain: srcChainId,
          srcTokenAddress: srcToken.address,
          dstChain: dstChainId,
          dstTokenAddress: dstToken.address,
          recipientAddress,
          fundAmount: dstAmount,
          contractAddress: contractAddress,
          onrampVendor
        };
      }
    case "custom":
      {
        return {
          type: "custom",
          srcChain: srcChainId,
          srcTokenAddress: srcToken.address,
          dstChain: dstChainId,
          dstTokenAddress: dstToken.address,
          recipientAddress,
          payload: {
            amount: dstAmount,
            data: encodedData,
            to: contractAddress,
            spenderAddress: spenderAddress
          },
          onrampVendor
        };
      }
    default:
      {
        throw new Error("Unsupported order type");
      }
  }
}
export function AnySpendCustom(props) {
  const fingerprintConfig = getFingerprintConfig();
  return _jsx(AnySpendFingerprintWrapper, {
    fingerprint: fingerprintConfig,
    children: _jsx(AnySpendCustomizationProvider, {
      slots: props.slots,
      content: props.content,
      theme: props.theme,
      children: _jsx(AnySpendCustomInner, {
        ...props
      })
    })
  });
}
function AnySpendCustomInner({
  loadOrder,
  mode = "modal",
  activeTab: activeTabProps = "crypto",
  recipientAddress: recipientAddressProps,
  spenderAddress,
  orderType,
  dstChainId,
  dstToken,
  dstAmount,
  contractAddress,
  encodedData,
  metadata,
  header,
  onSuccess,
  showRecipient = true,
  onShowPointsDetail,
  srcFiatAmount: srcFiatAmountProps,
  forceFiatPayment,
  showFiatOption = true,
  senderAddress
}) {
  const hasMounted = useHasMounted();
  const searchParams = useSearchParamsSSR();
  const router = useRouter();
  const [activePanel, setActivePanel] = useState(loadOrder ? PanelView.ORDER_DETAILS : PanelView.CONFIRM_ORDER);
  const [activeTab, setActiveTab] = useState(!showFiatOption ? "crypto" : forceFiatPayment ? "fiat" : activeTabProps);
  useEffect(() => {
    if (!showFiatOption && activeTab === "fiat") setActiveTab("crypto");
  }, [showFiatOption, activeTab]);
  // Payment method state with dual-state system (auto + explicit user selection)
  // Note: AnySpendCustom doesn't use auto-selection, only explicit user selection
  const {
    setSelectedCryptoPaymentMethod,
    effectiveCryptoPaymentMethod,
    resetPaymentMethods
  } = useCryptoPaymentMethodState();
  const [selectedFiatPaymentMethod, setSelectedFiatPaymentMethod] = useState(FiatPaymentMethod.NONE);
  // Get current user's wallet
  const currentWallet = useAccountWallet();
  // Recipient address state with dual-state system (auto + explicit user selection)
  // The hook automatically manages priority: props > user selection > global address
  const {
    setSelectedRecipientAddress,
    effectiveRecipientAddress: recipientAddress
  } = useRecipientAddressState({
    recipientAddressFromProps: recipientAddressProps,
    globalAddress: currentWallet.address
  });
  const [orderId, setOrderId] = useState(loadOrder);
  const [srcChainId, setSrcChainId] = useState(base.id);
  // Get token list for token balance check
  const chainName = useMemo(() => simpleHashChainToChainName(srcChainId), [srcChainId]);
  const {
    data: tokenList
  } = useAnyspendTokenList(srcChainId, "");
  // Get token balances for the selected chain
  const effectiveBalanceAddress = senderAddress || currentWallet?.wallet?.address || "";
  const {
    nativeTokens,
    fungibleTokens
  } = useTokenBalancesByChain({
    address: effectiveBalanceAddress,
    chainsIds: [srcChainId],
    enabled: !!effectiveBalanceAddress && !!chainName
  });
  // Find a token with a balance, prioritizing tokens the user already owns
  const getTokenWithBalance = useCallback(() => {
    if (!effectiveBalanceAddress || !nativeTokens && !fungibleTokens || !tokenList) {
      return getDefaultToken(srcChainId);
    }
    // First check native tokens (ETH, etc.)
    const nativeToken = nativeTokens?.find(t => t.chainId === srcChainId && Number(t.displayValue) > 0);
    if (nativeToken) {
      const matchingToken = tokenList.find(t_0 => t_0.address === ZERO_ADDRESS);
      if (matchingToken) return matchingToken;
    }
    // Then check ERC20 tokens
    if (fungibleTokens?.length) {
      // Find the token with the highest balance
      const tokensWithBalance = fungibleTokens.filter(t_1 => t_1.chain_id === srcChainId && Number(t_1.balance) > 0).sort((a, b) => Number(b.balance) - Number(a.balance));
      if (tokensWithBalance.length > 0) {
        // Extract the token address from fungible_id (chain.address)
        const topToken = tokensWithBalance[0];
        const tokenAddress = topToken.token_address;
        const matchingToken_0 = tokenList.find(t_2 => t_2.address.toLowerCase() === tokenAddress);
        if (matchingToken_0) return matchingToken_0;
      }
    }
    // Default fallback
    return getDefaultToken(srcChainId);
  }, [effectiveBalanceAddress, nativeTokens, fungibleTokens, tokenList, srcChainId]);
  // Set the selected token with preference for tokens user owns
  const [srcToken, setSrcToken] = useState(getDefaultToken(srcChainId));
  const [dirtySelectSrcToken, setDirtySelectSrcToken] = useState(false);
  // Update token when chain changes or token balances are loaded
  useEffect(() => {
    if (tokenList?.length && !dirtySelectSrcToken) {
      const tokenWithBalance = getTokenWithBalance();
      if (eqci(tokenWithBalance.address, dstToken.address) === false) {
        setSrcToken(tokenWithBalance);
      }
    }
  }, [srcChainId, tokenList, getTokenWithBalance, nativeTokens, fungibleTokens, dirtySelectSrcToken, dstToken.address]);
  const getRelayQuoteRequest = useMemo(() => {
    return generateGetRelayQuoteRequest({
      orderType: orderType,
      srcChainId: activeTab === "fiat" ? base.id : srcChainId,
      srcToken: activeTab === "fiat" ? USDC_BASE : srcToken,
      dstChainId: dstChainId,
      dstToken: dstToken,
      recipientAddress,
      dstAmount: dstAmount,
      contractAddress: contractAddress,
      tokenId: orderType === "mint_nft" ? metadata?.nftContract?.tokenId : undefined,
      contractType: orderType === "mint_nft" ? metadata?.nftContract?.type : undefined,
      encodedData: encodedData,
      spenderAddress: spenderAddress,
      onrampVendor: selectedFiatPaymentMethod === FiatPaymentMethod.STRIPE ? "stripe" : selectedFiatPaymentMethod === FiatPaymentMethod.STRIPE_WEB2 ? "stripe-web2" : undefined
    });
  }, [activeTab, contractAddress, dstAmount, dstChainId, dstToken, encodedData, metadata?.nftContract?.tokenId, metadata?.nftContract?.type, orderType, recipientAddress, spenderAddress, srcChainId, srcToken, selectedFiatPaymentMethod]);
  const {
    anyspendQuote,
    isLoadingAnyspendQuote
  } = useAnyspendQuote(getRelayQuoteRequest);
  const {
    orderAndTransactions: oat
  } = useAnyspendOrderAndTransactions(orderId);
  const onSelectOrder = selectedOrderId => {
    setActivePanel(PanelView.ORDER_DETAILS);
    setOrderId(selectedOrderId);
    // Update URL with the new orderId
    const params = new URLSearchParams(searchParams.toString());
    params.set("orderId", selectedOrderId);
    router.push(`${window.location.pathname}?${params.toString()}`);
  };
  // Update dependent amount when relay price changes
  const srcAmount = useMemo(() => {
    if (!anyspendQuote?.data || !anyspendQuote?.data?.currencyIn?.amount || !anyspendQuote?.data?.currencyIn?.currency?.decimals) return null;
    const amount = anyspendQuote.data.currencyIn.amount;
    if (activeTab === "fiat") {
      const roundUpAmount = roundUpUSDCBaseAmountToNearest(amount);
      return BigInt(roundUpAmount);
    } else {
      return BigInt(amount);
    }
  }, [activeTab, anyspendQuote?.data]);
  const formattedSrcAmount = srcAmount ? formatTokenAmount(srcAmount, srcToken.decimals, 6, false) : null;
  // Calculate fiat amount for geo check (regardless of activeTab) to determine tab availability
  const srcFiatAmountForGeoCheck = useMemo(() => {
    // Use prop if provided
    if (srcFiatAmountProps) {
      return srcFiatAmountProps;
    }
    // Fallback to dstAmount if destination token is USDC
    if (dstAmount && dstToken.address.toLowerCase() === USDC_BASE.address.toLowerCase()) {
      return formatUnits(dstAmount, USDC_BASE.decimals);
    }
    // Use srcAmount if available (from quote)
    if (srcAmount) {
      return formatUnits(srcAmount.toString(), USDC_BASE.decimals);
    }
    return "0";
  }, [srcAmount, srcFiatAmountProps, dstAmount, dstToken.address]);
  const srcFiatAmount = useMemo(() => {
    if (activeTab !== "fiat") return "0";
    return srcFiatAmountForGeoCheck;
  }, [activeTab, srcFiatAmountForGeoCheck]);
  // Get geo data and onramp options (use srcFiatAmountForGeoCheck to check availability regardless of activeTab)
  const {
    geoData,
    isOnrampSupported,
    coinbaseAvailablePaymentMethods,
    stripeOnrampSupport,
    stripeWeb2Support
  } = useGeoOnrampOptions(srcFiatAmountForGeoCheck);
  // Call onSuccess when order is executed
  useOnOrderSuccess({
    orderData: oat,
    orderId,
    onSuccess
  });
  const {
    createOrder: createRegularOrder,
    isCreatingOrder: isCreatingRegularOrder
  } = useAnyspendCreateOrder({
    onSuccess: data => {
      setOrderId(data.data.id);
    },
    onError: error => {
      console.error(error);
      toast.error("Failed to create order: " + error.message);
    }
  });
  const {
    createOrder: createOnrampOrder,
    isCreatingOrder: isCreatingOnrampOrder
  } = useAnyspendCreateOnrampOrder({
    onSuccess: data_0 => {
      setOrderId(data_0.data.id);
    },
    onError: error_0 => {
      console.error(error_0);
      toast.error("Failed to create order: " + error_0.message);
    }
  });
  const isCreatingOrder = isCreatingRegularOrder || isCreatingOnrampOrder;
  const {
    address: connectedAddress,
    name: connectedName
  } = useConnectedUserProfile(effectiveCryptoPaymentMethod);
  const recipientProfile = useProfile({
    address: recipientAddress
  });
  const recipientName = recipientProfile.data?.name;
  const handleCreateOrder = async (recipientAddress_0, onramp) => {
    try {
      invariant(anyspendQuote, "Relay price is not found");
      invariant(srcAmount, "Src amount is null");
      const createOrderParams = {
        orderType: orderType,
        srcChain: activeTab === "fiat" ? base.id : srcChainId,
        dstChain: dstChainId,
        srcToken: activeTab === "fiat" ? USDC_BASE : srcToken,
        dstToken: dstToken,
        srcAmount: srcAmount.toString(),
        recipientAddress: recipientAddress_0,
        creatorAddress: senderAddress || currentWallet?.wallet?.address,
        nft: orderType === "mint_nft" ? metadata.nftContract.type === "erc1155" ? {
          type: "erc1155",
          contractAddress: metadata.nftContract.contractAddress,
          tokenId: metadata.nftContract.tokenId ?? 0,
          name: metadata.nftContract.name,
          description: metadata.nftContract.description,
          imageUrl: metadata.nftContract.imageUrl,
          price: dstAmount
        } : {
          type: "erc721",
          contractAddress: metadata.nftContract.contractAddress,
          contractType: metadata.nftContract.type,
          price: dstAmount,
          name: metadata.nftContract.name,
          description: metadata.nftContract.description,
          imageUrl: metadata.nftContract.imageUrl
        } : undefined,
        tournament: orderType === "join_tournament" || orderType === "fund_tournament" ? {
          ...metadata.tournament,
          contractAddress: contractAddress,
          entryPriceOrFundAmount: dstAmount
        } : undefined,
        // only populate payload for custom tx
        payload: orderType === "custom" ? {
          amount: dstAmount,
          data: encodedData,
          spenderAddress: spenderAddress,
          to: contractAddress
        } : undefined,
        metadata
      };
      if (onramp) {
        const effectiveSrcToken = activeTab === "fiat" ? USDC_BASE : srcToken;
        invariant(effectiveSrcToken.address === USDC_BASE.address, "Selected src token is not USDC");
        invariant((activeTab === "fiat" ? base.id : srcChainId) === base.id, "Selected src chain is not base");
        // Get the current geo data from the hook
        const currentGeoData = geoData;
        // Use total amount from quote (includes fees) for onramp, fallback to srcFiatAmount
        const onrampAmount = anyspendQuote?.data?.currencyIn?.amountUsd ? anyspendQuote.data.currencyIn.amountUsd.toString() : srcFiatAmount;
        void createOnrampOrder({
          ...createOrderParams,
          srcFiatAmount: onrampAmount,
          onramp: {
            vendor: onramp.vendor,
            paymentMethod: onramp.paymentMethod,
            country: currentGeoData?.country || "US",
            redirectUrl: window.location.origin === "https://basement.fun" ? "https://basement.fun/deposit" : window.location.origin
          },
          expectedDstAmount: anyspendQuote?.data?.currencyOut?.amount?.toString() || "0"
        });
      } else {
        void createRegularOrder({
          ...createOrderParams
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message);
      throw err;
    }
  };
  const handleConfirmOrder = async onramp_0 => {
    // Check if recipient is selected
    if (!recipientAddress) {
      setActivePanel(PanelView.RECIPIENT_SELECTION);
      return;
    }
    // Check payment method selection for crypto tab
    if (activeTab === "crypto" && effectiveCryptoPaymentMethod === CryptoPaymentMethodType.NONE) {
      setActivePanel(PanelView.CRYPTO_PAYMENT_METHOD);
      return;
    }
    // Check payment method selection for fiat tab
    if (activeTab === "fiat" && selectedFiatPaymentMethod === FiatPaymentMethod.NONE) {
      setActivePanel(PanelView.FIAT_PAYMENT_METHOD);
      return;
    }
    if (recipientAddress) {
      try {
        await handleCreateOrder(recipientAddress, onramp_0);
      } catch (err_0) {
        console.error("Error creating order:", err_0);
        toast.error(`Error creating order: ${err_0 instanceof Error ? err_0.message : err_0}`);
      }
    }
  };
  // Handle fiat order creation
  const handleFiatOrder = async paymentMethod => {
    try {
      invariant(anyspendQuote, "Relay price is not found");
      invariant(recipientAddress, "Recipient address is not found");
      if (!srcFiatAmount || parseFloat(srcFiatAmount) <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }
      // Determine vendor and payment method string based on selected payment method
      let vendor;
      let paymentMethodString = "";
      if (paymentMethod === FiatPaymentMethod.COINBASE_PAY) {
        if (coinbaseAvailablePaymentMethods.length === 0) {
          toast.error("Coinbase Pay not available");
          return;
        }
        vendor = "coinbase";
        paymentMethodString = coinbaseAvailablePaymentMethods[0]?.id || "";
      } else if (paymentMethod === FiatPaymentMethod.STRIPE_WEB2) {
        // Stripe Web2 embedded payment
        if (!stripeWeb2Support || !stripeWeb2Support.isSupport) {
          toast.error("Stripe embedded payment not available");
          return;
        }
        vendor = "stripe-web2";
        paymentMethodString = "";
      } else if (paymentMethod === FiatPaymentMethod.STRIPE) {
        // Stripe redirect (one-click buy URL)
        if (!stripeOnrampSupport) {
          toast.error("Stripe redirect payment not available");
          return;
        }
        vendor = "stripe";
        paymentMethodString = "";
      } else {
        toast.error("Please select a payment method");
        return;
      }
      await handleCreateOrder(recipientAddress, {
        paymentMethod: paymentMethodString,
        vendor: vendor
      });
    } catch (err_1) {
      console.error(err_1);
      toast.error("Failed to create order: " + err_1.message);
    }
  };
  const recipientSection = showRecipient ? _jsxs(motion.div, {
    initial: false,
    animate: {
      opacity: hasMounted ? 1 : 0,
      y: hasMounted ? 0 : 20,
      filter: hasMounted ? "blur(0px)" : "blur(10px)"
    },
    transition: {
      duration: 0.3,
      delay: 0.2,
      ease: "easeInOut"
    },
    className: "flex w-full items-center justify-between gap-4",
    children: [_jsx("div", {
      className: "text-as-tertiary text-sm",
      children: orderType === "swap" ? "Recipient" : orderType === "mint_nft" ? "Receive NFT at" : orderType === "join_tournament" ? "Join for" : "Recipient"
    }), _jsxs("div", {
      className: "flex flex-wrap items-center justify-end gap-2",
      children: [recipientAddress ? _jsx("button", {
        className: cn("text-as-tertiary flex items-center gap-2 rounded-lg"),
        onClick: () => setActivePanel(PanelView.RECIPIENT_SELECTION),
        children: _jsx("div", {
          className: "text-as-tertiary flex items-center gap-1 text-sm",
          children: _jsx("span", {
            className: "whitespace-nowrap",
            children: recipientName ? formatUsername(recipientName) : shortenAddress(recipientAddress)
          })
        })
      }) : _jsx("button", {
        className: "text-as-primary/70 flex items-center gap-1 rounded-lg",
        onClick: () => setActivePanel(PanelView.RECIPIENT_SELECTION),
        children: _jsx("div", {
          className: "whitespace-nowrap text-sm font-medium",
          children: "Select recipient"
        })
      }), _jsx(ChevronRight, {
        className: "h-4 w-4 shrink-0"
      })]
    })]
  }, recipientAddress) : null;
  const historyView = _jsx("div", {
    className: cn("mx-auto flex w-full max-w-2xl flex-col items-center", mode === "modal" && "bg-b3-react-background"),
    children: _jsx(OrderHistory, {
      mode: mode,
      onBack: () => {
        setActivePanel(PanelView.HISTORY);
      },
      onSelectOrder: onSelectOrder
    })
  });
  const orderDetailsView = _jsxs("div", {
    className: cn("mx-auto flex w-full flex-col items-center gap-4 p-5", mode === "modal" && "bg-b3-react-background rounded-xl"),
    children: [oat && _jsx(OrderDetails, {
      mode: mode,
      order: oat.data.order,
      depositTxs: oat.data.depositTxs,
      relayTxs: oat.data.relayTxs,
      executeTx: oat.data.executeTx,
      refundTxs: oat.data.refundTxs,
      cryptoPaymentMethod: activeTab === "fiat" ? CryptoPaymentMethodType.NONE : effectiveCryptoPaymentMethod,
      selectedCryptoPaymentMethod: effectiveCryptoPaymentMethod,
      onPaymentMethodChange: method => {
        // When user explicitly changes payment method, set it as selected
        setSelectedCryptoPaymentMethod(method);
      },
      onBack: () => {
        setOrderId(undefined);
        setActivePanel(PanelView.CONFIRM_ORDER);
        // Reset payment methods when going back
        resetPaymentMethods();
        // Remove orderId from URL when canceling
        const params_0 = new URLSearchParams(searchParams.toString());
        params_0.delete("orderId");
        router.push(`${window.location.pathname}?${params_0.toString()}`);
      }
    }), mode === "page" && _jsx("div", {
      className: "h-12"
    })]
  });
  const loadingView = _jsxs("div", {
    className: cn("mx-auto flex w-full flex-col items-center gap-4 p-5", mode === "modal" && "bg-b3-react-background"),
    children: [_jsxs(Badge, {
      variant: "default",
      className: "bg-b3-react-muted/30 border-b3-react-border hover:bg-b3-react-muted/50 flex items-center gap-3 px-4 py-1 text-base transition-all",
      children: [_jsx(Loader2, {
        className: "text-b3-react-foreground size-4 animate-spin"
      }), _jsx(TextShimmer, {
        duration: 1,
        className: "font-sf-rounded text-base font-semibold",
        children: "Loading..."
      })]
    }), _jsxs("div", {
      className: "flex w-full flex-1 flex-col",
      children: [_jsxs("div", {
        className: "mb-4 flex flex-col gap-1",
        children: [_jsx(Skeleton, {
          className: "h-4 w-24"
        }), _jsxs("div", {
          className: "mt-2 flex items-center gap-2",
          children: [_jsx(Skeleton, {
            className: "h-8 w-48"
          }), _jsx(Skeleton, {
            className: "ml-4 h-8 w-32"
          })]
        }), _jsx(Skeleton, {
          className: "mt-4 h-8 w-24"
        })]
      }), _jsx(Skeleton, {
        className: "mb-4 h-12 w-full"
      }), _jsxs("div", {
        className: "flex w-full items-center justify-between gap-4",
        children: [_jsxs(Skeleton, {
          className: "rounded-lg bg-white/5 p-6 pb-3",
          children: [_jsx("div", {
            className: "size-[200px]"
          }), _jsx("div", {
            className: "mt-3 flex items-center justify-center gap-2",
            children: _jsx("div", {
              className: "size-5 rounded-full"
            })
          })]
        }), _jsx("div", {
          className: "flex flex-1 flex-col gap-2",
          children: [1, 2, 3].map(i => _jsx(Skeleton, {
            className: "h-10 w-full"
          }, i))
        })]
      })]
    }), _jsxs("div", {
      className: "bg-b3-react-muted/30 mt-8 w-full rounded-lg p-4",
      children: [_jsx(Skeleton, {
        className: "mb-3 h-4 w-48"
      }), _jsxs("div", {
        className: "flex items-center gap-4",
        children: [_jsx(Skeleton, {
          className: "h-10 flex-1"
        }), _jsx(Skeleton, {
          className: "h-10 flex-1"
        })]
      })]
    }), _jsx("div", {
      className: "flex w-full flex-col gap-3",
      children: [1, 2, 3, 4, 5].map(i_0 => _jsxs("div", {
        className: "flex w-full justify-between",
        children: [_jsx(Skeleton, {
          className: "h-4 w-24"
        }), _jsx(Skeleton, {
          className: "h-4 w-32"
        })]
      }, i_0))
    }), _jsx(Skeleton, {
      className: "h-10 w-full"
    }), mode === "page" && _jsx("div", {
      className: "h-12"
    })]
  });
  // Render points badge if conditions are met
  const renderPointsBadge = () => {
    if (anyspendQuote?.data?.pointsAmount && anyspendQuote.data.pointsAmount > 0) {
      return _jsx(PointsBadge, {
        pointsAmount: anyspendQuote.data.pointsAmount,
        pointsMultiplier: anyspendQuote.data.pointsMultiplier,
        onClick: () => {
          onShowPointsDetail?.();
          setActivePanel(PanelView.POINTS_DETAIL);
        }
      });
    }
    return null;
  };
  // Confirm order view.
  const confirmOrderView = _jsxs("div", {
    className: "relative mx-auto flex w-full flex-col items-center",
    children: [header({
      anyspendPrice: anyspendQuote,
      isLoadingAnyspendPrice: isLoadingAnyspendQuote
    }), _jsxs(Tabs, {
      value: activeTab,
      onValueChange: value => setActiveTab(value),
      className: "bg-b3-react-background max-h-[60dvh] w-full overflow-y-auto p-5",
      children: [!forceFiatPayment && showFiatOption && _jsx("div", {
        className: "w-full",
        children: _jsxs("div", {
          className: "bg-as-surface-secondary relative mb-4 grid h-10 grid-cols-2 rounded-xl",
          children: [_jsx("div", {
            className: cn("bg-as-brand absolute bottom-0 left-0 top-0 z-0 rounded-xl transition-transform duration-100", "h-full w-1/2", activeTab === "fiat" ? "translate-x-full" : "translate-x-0"),
            style: {
              willChange: "transform"
            }
          }), _jsx("button", {
            className: cn("relative z-10 h-full w-full rounded-xl px-3 text-sm font-medium transition-colors duration-100", activeTab === "crypto" ? "text-white" : "text-as-primary/70 hover:bg-as-on-surface-2 bg-transparent"),
            onClick: () => {
              setActiveTab("crypto");
              // Reset payment methods when switching tabs
              resetPaymentMethods();
              setSelectedFiatPaymentMethod(FiatPaymentMethod.NONE);
            },
            children: "Pay with crypto"
          }), isOnrampSupported ? _jsx("button", {
            className: cn("relative z-10 h-full w-full rounded-xl px-3 text-sm font-medium transition-colors duration-100", activeTab === "fiat" ? "text-white" : "text-as-primary/70 hover:bg-as-on-surface-2 bg-transparent"),
            onClick: () => {
              setActiveTab("fiat");
              // Reset payment methods when switching tabs
              resetPaymentMethods();
              setSelectedFiatPaymentMethod(FiatPaymentMethod.NONE);
            },
            children: "Pay with fiat"
          }) : _jsxs(Tooltip, {
            children: [_jsx(TooltipTrigger, {
              asChild: true,
              children: _jsx("button", {
                className: cn("relative z-10 h-full w-full rounded-xl px-3 text-sm font-medium transition-colors duration-100", "text-as-primary/50 cursor-not-allowed bg-transparent"),
                disabled: true,
                children: "Pay with fiat"
              })
            }), _jsx(TooltipContent, {
              children: _jsx("span", {
                className: "text-as-primary w-[140px]",
                children: "Fiat payments are not supported for this amount"
              })
            })]
          })]
        })
      }), _jsx(TabsContent, {
        value: "crypto",
        children: _jsxs("div", {
          className: "mt-2 flex flex-col gap-6",
          children: [_jsxs("div", {
            className: "border-as-border-secondary bg-as-surface-secondary flex w-full flex-col gap-4 rounded-xl border p-4",
            children: [_jsxs(motion.div, {
              initial: false,
              animate: {
                opacity: hasMounted ? 1 : 0,
                y: hasMounted ? 0 : 20,
                filter: hasMounted ? "blur(0px)" : "blur(10px)"
              },
              transition: {
                duration: 0.3,
                delay: 0,
                ease: "easeInOut"
              },
              className: "relative flex w-full items-center justify-between",
              children: [_jsx("div", {
                className: "text-as-tertiary flex h-7 items-center text-sm",
                children: "Pay"
              }), _jsx("button", {
                className: "text-as-tertiary flex flex-wrap items-center justify-end gap-2 text-sm transition-colors hover:text-blue-700",
                onClick: () => setActivePanel(PanelView.CRYPTO_PAYMENT_METHOD),
                children: _jsx(CryptoPaymentMethodDisplay, {
                  paymentMethod: effectiveCryptoPaymentMethod,
                  connectedAddress: connectedAddress,
                  connectedName: connectedName
                })
              })]
            }), _jsx("div", {
              className: "divider w-full"
            }), recipientSection, _jsx("div", {
              className: "divider w-full"
            }), _jsxs("div", {
              className: "flex flex-col gap-4",
              children: [_jsxs(motion.div, {
                initial: false,
                animate: {
                  opacity: hasMounted ? 1 : 0,
                  y: hasMounted ? 0 : 20,
                  filter: hasMounted ? "blur(0px)" : "blur(10px)"
                },
                transition: {
                  duration: 0.3,
                  delay: 0,
                  ease: "easeInOut"
                },
                className: "relative flex w-full items-center justify-between",
                children: [_jsx("div", {
                  className: "text-as-tertiary text-sm",
                  children: "Pay with"
                }), _jsx(OrderToken, {
                  address: effectiveBalanceAddress || undefined,
                  context: "from",
                  chainId: srcChainId,
                  setChainId: setSrcChainId,
                  token: srcToken,
                  setToken: token => {
                    setDirtySelectSrcToken(true);
                    setSrcToken(token);
                  },
                  requiredAmount: srcAmount || undefined
                })]
              }), _jsx("div", {
                className: "divider w-full"
              }), _jsxs(motion.div, {
                initial: false,
                animate: {
                  opacity: hasMounted ? 1 : 0,
                  y: hasMounted ? 0 : 20,
                  filter: hasMounted ? "blur(0px)" : "blur(10px)"
                },
                transition: {
                  duration: 0.3,
                  delay: 0.1,
                  ease: "easeInOut"
                },
                className: "relative flex w-full items-center justify-between gap-4",
                children: [_jsxs("span", {
                  className: "text-as-tertiary flex flex-wrap items-center gap-1.5 text-sm",
                  children: [_jsxs("span", {
                    className: "whitespace-nowrap",
                    children: ["Total ", _jsx("span", {
                      className: "text-as-tertiary",
                      children: "(with fee)"
                    })]
                  }), anyspendQuote?.data?.fee && _jsx(TooltipProvider, {
                    children: _jsxs(Tooltip, {
                      children: [_jsx(TooltipTrigger, {
                        asChild: true,
                        children: _jsx("button", {
                          className: "text-as-primary/40 hover:text-as-primary/60 transition-colors",
                          children: _jsx(Info, {
                            className: "h-4 w-4"
                          })
                        })
                      }), _jsx(TooltipContent, {
                        side: "top",
                        children: _jsx(FeeBreakDown, {
                          fee: anyspendQuote.data.fee
                        })
                      })]
                    })
                  })]
                }), _jsxs("div", {
                  className: "flex flex-col items-end gap-0.5",
                  children: [_jsxs("div", {
                    className: "flex flex-wrap items-center justify-end gap-2",
                    children: [renderPointsBadge(), _jsxs("span", {
                      className: "text-as-primary whitespace-nowrap font-semibold",
                      children: [formattedSrcAmount || "--", " ", srcToken.symbol]
                    })]
                  }), anyspendQuote?.data?.fee?.type === "standard_fee" && anyspendQuote.data.currencyIn?.amountUsd && _jsxs("span", {
                    className: "text-as-secondary text-xs",
                    children: ["incl. $", (Number(anyspendQuote.data.currencyIn.amountUsd) * anyspendQuote.data.fee.finalFeeBps / 10000).toFixed(2), " ", "fee"]
                  })]
                })]
              })]
            })]
          }), _jsx("div", {
            className: cn("flex w-full flex-col items-center justify-between gap-2"),
            children: _jsx(motion.div, {
              initial: false,
              animate: {
                opacity: hasMounted ? 1 : 0,
                y: hasMounted ? 0 : 20,
                filter: hasMounted ? "blur(0px)" : "blur(10px)"
              },
              transition: {
                duration: 0.3,
                delay: 0.3,
                ease: "easeInOut"
              },
              className: "flex w-full flex-col gap-2",
              children: _jsx(ShinyButton, {
                accentColor: "hsl(var(--as-brand))",
                textColor: "text-white",
                disabled: isCreatingOrder || isLoadingAnyspendQuote || !anyspendQuote,
                onClick: () => handleConfirmOrder(),
                className: "relative w-full",
                children: isCreatingOrder ? _jsxs("div", {
                  className: "flex items-center gap-2",
                  children: [_jsx(Loader2, {
                    className: "size-4 animate-spin"
                  }), _jsx("span", {
                    children: "Creating order..."
                  })]
                }) : isLoadingAnyspendQuote ? _jsxs("div", {
                  className: "flex items-center gap-2",
                  children: [_jsx(Loader2, {
                    className: "size-4 animate-spin"
                  }), _jsx("span", {
                    children: "Loading quote..."
                  })]
                }) : !recipientAddress ? "Select recipient" : effectiveCryptoPaymentMethod === CryptoPaymentMethodType.NONE ? "Choose payment method" : anyspendQuote ? _jsxs(_Fragment, {
                  children: [_jsx("span", {
                    children: "Checkout"
                  }), _jsx(ChevronRightCircle, {
                    className: "absolute right-0 top-1/2 size-6 -translate-y-1/2 opacity-70"
                  })]
                }) : "No quote found"
              })
            })
          })]
        })
      }), _jsx(TabsContent, {
        value: "fiat",
        children: _jsxs("div", {
          className: "mt-2 flex flex-col gap-6",
          children: [_jsxs("div", {
            className: "border-as-border-secondary bg-as-surface-secondary flex w-full flex-col gap-4 rounded-xl border p-4",
            children: [_jsxs(motion.div, {
              initial: false,
              animate: {
                opacity: hasMounted ? 1 : 0,
                y: hasMounted ? 0 : 20,
                filter: hasMounted ? "blur(0px)" : "blur(10px)"
              },
              transition: {
                duration: 0.3,
                delay: 0,
                ease: "easeInOut"
              },
              className: "relative flex w-full items-center justify-between",
              children: [_jsx("div", {
                className: "text-as-tertiary flex h-7 items-center text-sm",
                children: "Pay with"
              }), _jsx("button", {
                className: "text-as-tertiary flex flex-wrap items-center justify-end gap-2 text-sm transition-colors hover:text-blue-700",
                onClick: () => setActivePanel(PanelView.FIAT_PAYMENT_METHOD),
                children: (() => {
                  const config = FIAT_PAYMENT_METHOD_DISPLAY[selectedFiatPaymentMethod];
                  if (config) {
                    return _jsxs(_Fragment, {
                      children: [_jsxs("div", {
                        className: "flex items-center gap-2 whitespace-nowrap",
                        children: [_jsx("div", {
                          className: "flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600",
                          children: _jsx("span", {
                            className: "text-xs font-bold text-white",
                            children: config.icon
                          })
                        }), config.label]
                      }), _jsx(ChevronRight, {
                        className: "h-4 w-4 shrink-0"
                      })]
                    });
                  }
                  return _jsxs(_Fragment, {
                    children: [_jsx("span", {
                      className: "whitespace-nowrap",
                      children: "Select payment method"
                    }), _jsx(ChevronRight, {
                      className: "h-4 w-4 shrink-0"
                    })]
                  });
                })()
              })]
            }), _jsx("div", {
              className: "divider w-full"
            }), recipientSection, _jsx("div", {
              className: "divider w-full"
            }), _jsxs(motion.div, {
              initial: false,
              animate: {
                opacity: hasMounted ? 1 : 0,
                y: hasMounted ? 0 : 20,
                filter: hasMounted ? "blur(0px)" : "blur(10px)"
              },
              transition: {
                duration: 0.3,
                delay: 0.1,
                ease: "easeInOut"
              },
              className: "relative flex w-full items-center justify-between gap-4",
              children: [_jsxs("span", {
                className: "text-as-tertiary flex flex-wrap items-center gap-1.5 text-sm",
                children: [_jsxs("span", {
                  className: "whitespace-nowrap",
                  children: ["Total ", _jsx("span", {
                    className: "text-as-tertiary",
                    children: "(USD)"
                  })]
                }), anyspendQuote?.data?.fee && _jsx(TooltipProvider, {
                  children: _jsxs(Tooltip, {
                    children: [_jsx(TooltipTrigger, {
                      asChild: true,
                      children: _jsx("button", {
                        className: "text-as-primary/40 hover:text-as-primary/60 transition-colors",
                        children: _jsx(Info, {
                          className: "h-4 w-4"
                        })
                      })
                    }), _jsx(TooltipContent, {
                      side: "top",
                      children: _jsx(FeeBreakDown, {
                        fee: anyspendQuote.data.fee
                      })
                    })]
                  })
                })]
              }), _jsxs("div", {
                className: "flex flex-col items-end gap-0.5",
                children: [_jsxs("div", {
                  className: "flex flex-wrap items-center justify-end gap-2",
                  children: [renderPointsBadge(), isLoadingAnyspendQuote ? _jsx("div", {
                    className: "bg-as-surface-secondary h-7 w-16 animate-pulse rounded"
                  }) : _jsx("span", {
                    className: "text-as-primary whitespace-nowrap text-xl font-semibold",
                    children: anyspendQuote?.data?.currencyIn?.amountUsd ? `$${Number(anyspendQuote.data.currencyIn.amountUsd).toFixed(2)}` : _jsxs(_Fragment, {
                      children: ["$", parseFloat(srcFiatAmount || "0").toFixed(2), _jsx("span", {
                        className: "text-as-tertiary text-base",
                        children: "+"
                      })]
                    })
                  })]
                }), (() => {
                  if (anyspendQuote?.data?.fee?.type === "stripeweb2_fee" && anyspendQuote.data.fee.originalAmount) {
                    const fee = (Number(anyspendQuote.data.fee.originalAmount) - Number(anyspendQuote.data.fee.finalAmount)) / 1e6;
                    if (fee > 0) {
                      return _jsxs("span", {
                        className: "text-as-secondary text-xs",
                        children: ["incl. $", fee.toFixed(2), " fee"]
                      });
                    }
                  }
                  return null;
                })()]
              })]
            })]
          }), _jsx("div", {
            className: cn("flex w-full flex-col items-center justify-between gap-2"),
            children: _jsx(motion.div, {
              initial: false,
              animate: {
                opacity: hasMounted ? 1 : 0,
                y: hasMounted ? 0 : 20,
                filter: hasMounted ? "blur(0px)" : "blur(10px)"
              },
              transition: {
                duration: 0.3,
                delay: 0.3,
                ease: "easeInOut"
              },
              className: "flex w-full flex-col gap-2",
              children: _jsx(ShinyButton, {
                accentColor: "hsl(var(--as-brand))",
                textColor: "text-white",
                disabled: isCreatingOrder || isLoadingAnyspendQuote || !anyspendQuote,
                onClick: () => {
                  if (selectedFiatPaymentMethod !== FiatPaymentMethod.NONE) {
                    handleFiatOrder(selectedFiatPaymentMethod);
                  } else {
                    handleConfirmOrder();
                  }
                },
                className: "relative w-full",
                children: isCreatingOrder ? _jsxs("div", {
                  className: "flex items-center gap-2",
                  children: [_jsx(Loader2, {
                    className: "size-4 animate-spin"
                  }), _jsx("span", {
                    children: "Creating order..."
                  })]
                }) : isLoadingAnyspendQuote ? _jsxs("div", {
                  className: "flex items-center gap-2",
                  children: [_jsx(Loader2, {
                    className: "size-4 animate-spin"
                  }), _jsx("span", {
                    children: "Loading quote..."
                  })]
                }) : !recipientAddress ? "Select recipient" : selectedFiatPaymentMethod === FiatPaymentMethod.NONE ? "Select payment method" : anyspendQuote ? _jsxs(_Fragment, {
                  children: [_jsx("span", {
                    children: "Buy"
                  }), _jsx(ChevronRightCircle, {
                    className: "absolute right-0 top-1/2 size-6 -translate-y-1/2 opacity-70"
                  })]
                }) : "No quote found"
              })
            })
          })]
        })
      })]
    })]
  });
  // Recipient selection view
  const recipientSelectionView = _jsx("div", {
    className: cn("bg-as-surface-primary mx-auto w-full max-w-[460px] rounded-xl p-4"),
    children: _jsx(RecipientSelection, {
      initialValue: recipientAddress || "",
      title: "Add recipient address or ENS",
      description: "Send tokens to another address",
      onBack: () => setActivePanel(PanelView.CONFIRM_ORDER),
      onConfirm: address => {
        // User manually selected a recipient
        setSelectedRecipientAddress(address);
        setActivePanel(PanelView.CONFIRM_ORDER);
      }
    })
  });
  // Crypto payment method view
  const cryptoPaymentMethodView = _jsx("div", {
    className: cn("bg-as-surface-primary mx-auto w-full max-w-[460px] rounded-xl p-4"),
    children: _jsx(CryptoPaymentMethod, {
      selectedPaymentMethod: effectiveCryptoPaymentMethod,
      setSelectedPaymentMethod: method_0 => {
        // When user explicitly selects a payment method, save it
        setSelectedCryptoPaymentMethod(method_0);
      },
      isCreatingOrder: isCreatingOrder,
      onBack: () => setActivePanel(PanelView.CONFIRM_ORDER),
      onSelectPaymentMethod: method_1 => {
        // When user explicitly selects a payment method, save it and go back
        setSelectedCryptoPaymentMethod(method_1);
        setActivePanel(PanelView.CONFIRM_ORDER);
      }
    })
  });
  // Stable callback for fiat payment method selection
  const handleFiatPaymentMethodSelect = useCallback(method_2 => {
    setSelectedFiatPaymentMethod(method_2);
    setActivePanel(PanelView.CONFIRM_ORDER);
  }, []);
  // Stable callback for navigating back to confirm order
  const handleBackToConfirmOrder = useCallback(() => {
    setActivePanel(PanelView.CONFIRM_ORDER);
  }, []);
  // Fiat payment method view
  const fiatPaymentMethodView = _jsx("div", {
    className: cn("bg-as-surface-primary mx-auto w-full max-w-[460px] rounded-xl p-4"),
    children: _jsx(FiatPaymentMethodComponent, {
      selectedPaymentMethod: selectedFiatPaymentMethod,
      setSelectedPaymentMethod: setSelectedFiatPaymentMethod,
      onBack: handleBackToConfirmOrder,
      onSelectPaymentMethod: handleFiatPaymentMethodSelect,
      srcAmountOnRamp: srcFiatAmount
    })
  });
  // Points detail view
  const pointsDetailView = _jsx("div", {
    className: cn("bg-as-surface-primary mx-auto w-full max-w-[460px] rounded-xl p-4"),
    children: _jsx(PointsDetailPanel, {
      pointsAmount: anyspendQuote?.data?.pointsAmount || 0,
      onBack: () => setActivePanel(PanelView.CONFIRM_ORDER)
    })
  });
  // Return the TransitionPanel with all views
  return _jsx(StyleRoot, {
    children: _jsx(TransitionPanel, {
      activeIndex: orderId ? oat ? PanelView.ORDER_DETAILS : PanelView.LOADING : activePanel === PanelView.ORDER_DETAILS ? PanelView.CONFIRM_ORDER : activePanel,
      className: cn("anyspend-container font-inter w-full"),
      variants: {
        enter: {
          x: 300,
          opacity: 0
        },
        center: {
          x: 0,
          opacity: 1
        },
        exit: {
          x: -300,
          opacity: 0
        }
      },
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      },
      children: [_jsx("div", {
        className: "w-full",
        children: confirmOrderView
      }, "confirm-order-view"), _jsx("div", {
        className: "w-full",
        children: historyView
      }, "history-view"), _jsx("div", {
        className: "w-full",
        children: orderDetailsView
      }, "order-details-view"), _jsx("div", {
        className: "w-full",
        children: loadingView
      }, "loading-view"), _jsx("div", {
        className: "w-full",
        children: recipientSelectionView
      }, "recipient-selection-view"), _jsx("div", {
        className: "w-full",
        children: cryptoPaymentMethodView
      }, "crypto-payment-method-view"), _jsx("div", {
        className: "w-full",
        children: fiatPaymentMethodView
      }, "fiat-payment-method-view"), _jsx("div", {
        className: "w-full",
        children: pointsDetailView
      }, "points-detail-view")]
    })
  });
}